import React, { useState, useRef, useCallback, useEffect } from 'react';
import { X, AlertTriangle, Scale, Shield, Brain, ChevronDown } from 'lucide-react';
import axios from 'axios';

import API from '../utils/api';

export function TosModal({ open, onClose, onAccept, mode }) {
  var [sections, setSections] = useState([]);
  var [version, setVersion] = useState('');
  var [scrolledToBottom, setScrolledToBottom] = useState(false);
  var [checked, setChecked] = useState(false);
  var [arbChecked, setArbChecked] = useState(false);
  var [marketingChecked, setMarketingChecked] = useState(false);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState('');
  var scrollRef = useRef(null);

  useEffect(function () {
    if (!open) return;
    setScrolledToBottom(false);
    setChecked(false);
    setArbChecked(false);
    setMarketingChecked(false);
    setError('');
    setLoading(true);
    axios.get(API + '/tos/current')
      .then(function (r) {
        setSections(r.data.sections || []);
        setVersion(r.data.version || '1.0');
        setLoading(false);
      })
      .catch(function () {
        setError('Unable to load Terms of Service. Please try again.');
        setLoading(false);
      });
  }, [open]);

  var handleScroll = useCallback(function () {
    var el = scrollRef.current;
    if (!el) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 40) {
      setScrolledToBottom(true);
    }
  }, []);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="tos-modal">
      <div className="bg-[#FFFFFF] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-r from-[#2D2560] to-[#5A4E9E] flex items-center justify-center">
              <Scale className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-base font-bold">Terms of Service</h2>
              <p className="text-xs text-white/30">Version {version} | Effective 04/12/2026</p>
            </div>
          </div>
          {mode !== 'signup' && mode !== 'reaccept' && (
            <button onClick={onClose} className="text-white/30 hover:text-white/60 p-1" data-testid="tos-modal-close">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Scrollable content */}
        <div ref={scrollRef} onScroll={handleScroll} className="flex-1 overflow-y-auto px-6 py-4 space-y-4 min-h-0" data-testid="tos-content">
          {loading && <div className="py-12 text-center text-white/40 text-sm">Loading Terms of Service...</div>}
          {error && (
            <div className="py-12 text-center">
              <AlertTriangle className="w-8 h-8 text-[#EF4444] mx-auto mb-2" />
              <p className="text-sm text-[#EF4444]">{error}</p>
            </div>
          )}
          {!loading && !error && sections.map(function (s) {
            var isHighlight = s.highlight;
            return (
              <div key={s.id} className={'rounded-xl p-4 ' + (isHighlight ? 'bg-[#F59E0B]/[0.06] border border-[#F59E0B]/20' : 'bg-white/[0.02] border border-white/[0.04]')} data-testid={'tos-section-' + s.id}>
                <h3 className={'text-sm font-bold mb-2 ' + (isHighlight ? 'text-[#F59E0B]' : 'text-white/80')}>
                  {isHighlight && <AlertTriangle className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />}
                  {s.title}
                </h3>
                <p className="text-xs text-white/50 leading-relaxed whitespace-pre-line">{s.content}</p>
              </div>
            );
          })}
          {!loading && !error && !scrolledToBottom && (
            <div className="sticky bottom-0 flex justify-center pb-2">
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#5A4E9E]/20 border border-[#5A4E9E]/30 text-xs text-[#5A4E9E] font-medium">
                <ChevronDown className="w-3 h-3 animate-bounce" /> Scroll to read all terms
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/10 flex-shrink-0 space-y-3">
          <label className={'flex items-start gap-3 cursor-pointer ' + (!scrolledToBottom ? 'opacity-40 pointer-events-none' : '')} data-testid="tos-checkbox-label">
            <input
              type="checkbox"
              checked={checked}
              onChange={function (e) { setChecked(e.target.checked); }}
              disabled={!scrolledToBottom}
              className="mt-0.5 w-4 h-4 rounded border-white/20 bg-white/5 text-[#5A4E9E] focus:ring-[#5A4E9E]"
              data-testid="tos-checkbox"
            />
            <span className="text-xs text-white/60 leading-relaxed">
              I have read and agree to the <strong className="text-white/80">Terms of Service</strong> and <strong className="text-white/80">Privacy Policy</strong>.
            </span>
          </label>
          <label className={'flex items-start gap-3 cursor-pointer ' + (!scrolledToBottom ? 'opacity-40 pointer-events-none' : '')} data-testid="arbitration-checkbox-label">
            <input
              type="checkbox"
              checked={arbChecked}
              onChange={function (e) { setArbChecked(e.target.checked); }}
              disabled={!scrolledToBottom}
              className="mt-0.5 w-4 h-4 rounded border-white/20 bg-white/5 text-[#F59E0B] focus:ring-[#F59E0B]"
              data-testid="arbitration-checkbox"
            />
            <span className="text-xs text-white/60 leading-relaxed">
              I agree to resolve disputes via <strong className="text-[#F59E0B]">binding individual arbitration</strong> and waive my right to a jury trial and class action participation (Section 17).
            </span>
          </label>
          <label className={'flex items-start gap-3 cursor-pointer ' + (!scrolledToBottom ? 'opacity-40 pointer-events-none' : '')} data-testid="marketing-checkbox-label">
            <input
              type="checkbox"
              checked={marketingChecked}
              onChange={function (e) { setMarketingChecked(e.target.checked); }}
              disabled={!scrolledToBottom}
              className="mt-0.5 w-4 h-4 rounded border-white/20 bg-white/5 text-[#5A4E9E] focus:ring-[#5A4E9E]"
              data-testid="marketing-checkbox"
            />
            <span className="text-xs text-white/60 leading-relaxed">
              I consent to receive marketing communications from Parentum via <strong className="text-white/80">email</strong>, <strong className="text-white/80">SMS/text message</strong>, and <strong className="text-white/80">in-app notifications</strong>. You can opt out at any time. <span className="text-white/30">(Optional)</span>
            </span>
          </label>
          <button
            onClick={function () { if (checked && arbChecked && onAccept) onAccept(version, marketingChecked); }}
            disabled={!checked || !arbChecked || !!error}
            className="w-full py-2.5 rounded-lg text-sm font-semibold transition-all disabled:opacity-30 disabled:cursor-not-allowed bg-gradient-to-r from-[#2D2560] to-[#5A4E9E] text-white hover:opacity-90"
            data-testid="tos-accept-btn"
          >
            {mode === 'reaccept' ? 'Accept Updated Terms' : 'I Accept'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function TosLink({ className }) {
  var [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={function () { setOpen(true); }} className={className || 'text-[#5A4E9E] hover:text-[#2D2560] text-xs transition-colors'} data-testid="tos-link">
        Terms of Service
      </button>
      <TosModal open={open} onClose={function () { setOpen(false); }} mode="view" />
    </>
  );
}
